import React from 'react';
import { formatMrn } from '../../../utils/formatMrn';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Label } from "../../ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../ui/select";
import axios from "axios";
import { formatClientNameOrdered } from '../../../utils/nameFormatter';
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper to format time in 12-hour format
const formatTime12Hour = (timeStr) => {
  if (!timeStr) return '';
  try {
    const [hours, minutes] = timeStr.split(':');
    const hour = parseInt(hours, 10);
    const ampm = hour >= 12 ? 'PM' : 'AM';
    const displayHour = hour % 12 || 12;
    return `${displayHour}:${minutes} ${ampm}`;
  } catch {
    return timeStr;
  }
};

// Helper to format client name
const formatClientName = (client) => {
  if (!client) return 'Unknown Client';
  return formatClientNameOrdered(client, 'last_first')
    || client.preferred_name || 'Unknown Client';
};

export const GroupRequestDialog = ({
  open,
  onOpenChange,
  requestGroupData,
  setRequestGroupData,
  clients,
  clientGroups,
  onSuccess,  // Called after successful submission to refresh data
  selectedClient  // Optional: pre-selected client from client chart
}) => {
  // Ensure selected client is in the list for display
  const clientsForSelect = React.useMemo(() => {
    const clientList = clients || [];
    
    // If there's a pre-selected client_id and it's not in the clients array,
    // we need to add a placeholder entry so the Select can display it
    if (requestGroupData.client_id && !clientList.find(c => c.id === requestGroupData.client_id)) {
      // Check if selectedClient prop has the data
      if (selectedClient && selectedClient.id === requestGroupData.client_id) {
        return [selectedClient, ...clientList];
      }
    }
    
    return clientList;
  }, [clients, requestGroupData.client_id, selectedClient]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!requestGroupData.client_id || !requestGroupData.group_id) {
      toast.error("Please select both a client and a group");
      return;
    }
    
    try {
      await axios.post(`${API}/group-admission-requests`, {
        client_id: requestGroupData.client_id,
        group_id: requestGroupData.group_id,
        request_reason: requestGroupData.request_reason || ''
      });
      
      toast.success("Group placement request submitted!");
      onOpenChange(false);
      setRequestGroupData({ client_id: '', group_id: '', request_reason: '' });
      if (onSuccess) onSuccess();
    } catch (error) {
      console.error("Failed to submit request:", error);
      toast.error("Failed to submit request");
    }
  };

  const handleCancel = () => {
    onOpenChange(false);
    setRequestGroupData({ client_id: '', group_id: '', request_reason: '' });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Request Client Group Placement</DialogTitle>
        </DialogHeader>
        
        <form className="space-y-4" onSubmit={handleSubmit}>
          <div className="bg-blue-50 border border-blue-200 rounded-md p-3 text-sm">
            <p className="text-blue-800">
              <strong>Note:</strong> Submit a request for a client to be added to a group. 
              Group admins will review and approve/deny your request.
            </p>
          </div>

          <div>
            <Label htmlFor="request-client">Select Client *</Label>
            <Select
              value={requestGroupData.client_id}
              onValueChange={(value) => setRequestGroupData({...requestGroupData, client_id: value})}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a client..." />
              </SelectTrigger>
              <SelectContent>
                {clientsForSelect.length === 0 ? (
                  <div className="px-2 py-4 text-center text-gray-500">
                    Loading clients...
                  </div>
                ) : (
                  clientsForSelect
                    .filter(c => c.status !== 'archived')
                    .sort((a, b) => (a.last_name || a.full_name || '').localeCompare(b.last_name || b.full_name || ''))
                    .map(client => (
                      <SelectItem key={client.id} value={client.id}>
                        {formatClientName(client)}
                        {client.mrn && <span className="text-gray-500 ml-2">MRN: {formatMrn(client.mrn)}</span>}
                      </SelectItem>
                    ))
                )}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="request-group">Select Group *</Label>
            <Select
              value={requestGroupData.group_id}
              onValueChange={(value) => setRequestGroupData({...requestGroupData, group_id: value})}
            >
              <SelectTrigger>
                <SelectValue placeholder="Choose a group..." />
              </SelectTrigger>
              <SelectContent>
                {clientGroups
                  .filter(g => g.status === 'active' && g.is_active !== false)
                  .map(group => (
                    <SelectItem key={group.id} value={group.id}>
                      {group.name}
                      {group.schedule_day && group.start_time && (
                        <span className="text-gray-500 ml-2">
                          {group.schedule_day}s {formatTime12Hour(group.start_time)}
                        </span>
                      )}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="request-reason">Reason for Request (Optional)</Label>
            <textarea
              id="request-reason"
              className="w-full border border-gray-300 rounded-md p-2 min-h-[100px] focus:outline-none focus:ring-2 focus:ring-teal-500"
              placeholder="Explain why this client would benefit from this group..."
              value={requestGroupData.request_reason}
              onChange={(e) => setRequestGroupData({...requestGroupData, request_reason: e.target.value})}
            />
          </div>

          <div className="flex justify-end space-x-2 pt-4">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              className="bg-teal-600 hover:bg-teal-700"
              disabled={!requestGroupData.client_id || !requestGroupData.group_id}
            >
              Submit Request
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default GroupRequestDialog;
